import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { EMPTY_LIST } from '../../../constants/common'
import { getItemsFromIdwellApiResponse } from '../../../utils/api'
import { Divider } from '../../Divider'
import { FieldStackedLabel } from '../../Field'
import { FlatSelectFormikField } from '../../Flat'
import { FormFieldsContainer, FormFieldSelectNG } from '../../Form'
import { FormikField, getFormikFormFieldErrorProps } from '../../Formik'
import {
  toUserSelectItemsFromOption,
  UserMultiSelect,
  UserSelect,
} from '../../User'
import { DocFlowBuildingField } from '../DocFlowBuildingField'
import { DocFlowBuildingGroupField } from '../DocFlowBuildingGroupField'

import { getRecipients } from './DocFlowAddRecipientFields.api'
import {
  DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_BUILDING,
  DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_BUILDING_GROUP,
  DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_FLAT,
  DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_RECIPIENT,
} from './DocFlowAddRecipientFields.constants'
import { isRecipientValue } from './DocFlowAddRecipientFields.utils'

import * as styles from './DocFlowAddRecipientFields.module.css'

function DocFlowAddRecipientFields({
  documentId,
  edit,
  recipientFieldName = DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_RECIPIENT,
  ...restProps
}) {
  const { t } = useTranslation()

  const { initialValues, setFieldValue, values } = useFormikContext()

  const buildingValue =
    values[DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_BUILDING]?.value
  const buildingGroupValue =
    values[DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_BUILDING_GROUP]?.value
  const flatValue = values[DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_FLAT]?.value

  useEffect(() => {
    if (isRecipientValue({ edit, values }) && buildingValue) {
      setFieldValue(recipientFieldName, initialValues[recipientFieldName])
    }
  }, [buildingValue])

  const recipientsFieldApiProps = {
    [DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_BUILDING]: buildingValue,
    [DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_BUILDING_GROUP]:
      buildingGroupValue,
    [DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_FLAT]: flatValue,
    documentId,
  }

  const buldingApiParams = buildingGroupValue
    ? {
        [DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_BUILDING_GROUP]:
          buildingGroupValue,
      }
    : null

  const fieldApiParamsByBuilding = buildingValue
    ? {
        [DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_BUILDING]: buildingValue,
      }
    : null

  return (
    <FormFieldsContainer>
      <DocFlowBuildingGroupField
        {...restProps}
        apiParams={fieldApiParamsByBuilding}
        inputInfo={t('DocFlow.AddRecipientBuildingGroupFieldInfoKey')}
        inputLabel={t('DocFlow.AddRecipientBuildingGroupFieldLabelKey')}
        placeholder={t('DocFlow.AddRecipientBuildingGroupFieldPlaceholderKey')}
      />
      <DocFlowBuildingField
        {...restProps}
        apiParams={buldingApiParams}
        inputInfo={t('DocumentStapling.AddRecipientBuildingFieldInfoKey')}
        inputLabel={t('DocumentStapling.AddRecipientBuildingFieldLabelKey')}
        placeholder={t(
          'DocumentStapling.AddRecipientBuildingFieldPlaceholderKey'
        )}
      />
      <FlatSelectFormikField
        {...restProps}
        Field={FieldStackedLabel}
        apiParams={fieldApiParamsByBuilding}
        controlProps={{
          contentProps: {
            inputProps: {
              placeholder: t('DocFlow.AddRecipientFlatFieldPlaceholderKey'),
            },
          },
        }}
        disabled={!buildingValue}
        emptyValue={null}
        fieldComponentProps={{
          info: t('DocFlow.AddRecipientFlatFieldInfoKey'),
          label: t('DocFlow.AddRecipientFlatFieldLabelKey'),
        }}
        name={DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_FLAT}
      />
      <Divider className={styles.divider} />
      <FormikField
        {...restProps}
        {...(!edit
          ? {
              emptyValue: EMPTY_LIST,
              multi: true,
            }
          : null)}
        Component={FormFieldSelectNG}
        Field={FieldStackedLabel}
        Select={edit ? UserSelect : UserMultiSelect}
        apiParams={recipientsFieldApiProps}
        controlProps={{
          contentProps: {
            inputProps: {
              placeholder: t(
                'DocumentStapling.AddRecipientFieldPlaceholderKey'
              ),
            },
          },
        }}
        fieldComponentProps={{
          label: t('DocumentStapling.AddRecipientFieldLabelKey'),
          required: true,
        }}
        getData={getRecipients}
        getDataItems={getItemsFromIdwellApiResponse}
        getErrorProps={getFormikFormFieldErrorProps}
        name={recipientFieldName}
        toPreparedItems={toUserSelectItemsFromOption}
      />
    </FormFieldsContainer>
  )
}

export default DocFlowAddRecipientFields
